import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import Button from "../Button";
import { useTranslation } from "react-i18next";

export type Ampm = "AM" | "PM";

const ITEM_HEIGHT = 52;
const LOOP_REPEAT_COUNT = 21;

const pad = (value: number) => value.toString().padStart(2, "0");

const normalizeMinuteStep = (minute: number) =>
  Math.max(0, Math.min(55, Math.round(minute / 5) * 5));

const to12Hour = (hour24: number) => {
  const ampm: Ampm = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;
  return { hour12, ampm };
};

const clampIndex = (index: number, length: number) =>
  Math.max(0, Math.min(length - 1, index));

const normalizeIndex = (index: number, length: number) => {
  if (length <= 0) return 0;
  return ((index % length) + length) % length;
};

function WheelColumn({
  label,
  items,
  value,
  onChange,
  initialIndex,
  resetKey,
}: {
  label: string;
  items: string[];
  value: string;
  onChange: (value: string) => void;
  initialIndex: number;
  resetKey: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollTimerRef = useRef<number | null>(null);
  const [spacerHeight, setSpacerHeight] = useState(ITEM_HEIGHT * 2);
  const loopItems = useMemo(() => {
    if (items.length === 0) return [];
    return Array.from({ length: LOOP_REPEAT_COUNT }, () => items).flat();
  }, [items]);
  const centerLoopStart = items.length * Math.floor(LOOP_REPEAT_COUNT / 2);

  const scrollToIndex = (
    index: number,
    maxIndex: number,
    behavior: ScrollBehavior = "smooth",
  ) => {
    const element = containerRef.current;
    if (!element) return;

    element.scrollTo({
      top: clampIndex(index, maxIndex) * ITEM_HEIGHT,
      behavior,
    });
  };

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const measure = () => {
      const nextSpacer = Math.max(
        ITEM_HEIGHT,
        Math.floor((element.clientHeight - ITEM_HEIGHT) / 2),
      );
      setSpacerHeight(nextSpacer);
      element.scrollTop =
        (centerLoopStart + normalizeIndex(initialIndex, items.length)) *
        ITEM_HEIGHT;
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [initialIndex, items, resetKey]);

  useEffect(() => {
    return () => {
      if (scrollTimerRef.current !== null) {
        window.clearTimeout(scrollTimerRef.current);
      }
    };
  }, []);

  const syncSelectionFromScroll = (shouldRecenter: boolean) => {
    const element = containerRef.current;
    if (!element || items.length === 0) return;

    const rawIndex = Math.round(element.scrollTop / ITEM_HEIGHT);
    const normalizedItemIndex = normalizeIndex(rawIndex, items.length);
    const nextValue = items[normalizedItemIndex];

    if (nextValue && nextValue !== value) {
      onChange(nextValue);
    }

    if (!shouldRecenter) return;

    const recenteredIndex = centerLoopStart + normalizedItemIndex;
    const nextScrollTop = recenteredIndex * ITEM_HEIGHT;

    if (Math.abs(element.scrollTop - nextScrollTop) > 1) {
      element.scrollTo({ top: nextScrollTop, behavior: "auto" });
    }
  };

  const settleScroll = () => {
    const element = containerRef.current;
    if (!element || items.length === 0) return;

    const rawIndex = Math.round(element.scrollTop / ITEM_HEIGHT);
    const recenteredIndex = centerLoopStart + normalizeIndex(rawIndex, items.length);

    syncSelectionFromScroll(false);

    const nextScrollTop = recenteredIndex * ITEM_HEIGHT;
    if (Math.abs(element.scrollTop - nextScrollTop) > 1) {
      element.scrollTo({ top: nextScrollTop, behavior: "auto" });
    }
  };

  const handleScroll = () => {
    const element = containerRef.current;
    if (!element || items.length === 0) return;

    syncSelectionFromScroll(false);

    const rawIndex = Math.round(element.scrollTop / ITEM_HEIGHT);
    const lowerBound = items.length * 4;
    const upperBound = items.length * (LOOP_REPEAT_COUNT - 4);

    if (rawIndex < lowerBound || rawIndex >= upperBound) {
      syncSelectionFromScroll(true);
    }

    if (scrollTimerRef.current !== null) {
      window.clearTimeout(scrollTimerRef.current);
    }

    scrollTimerRef.current = window.setTimeout(settleScroll, 80);
  };

  const scrollToLoopedIndex = (
    index: number,
    behavior: ScrollBehavior = "smooth",
  ) => {
    const element = containerRef.current;
    if (!element || items.length === 0) return;

    const rawIndex = Math.round(element.scrollTop / ITEM_HEIGHT);
    const currentLoop = Math.round((rawIndex - index) / items.length);
    const targetIndex = clampIndex(
      index + currentLoop * items.length,
      loopItems.length - 1,
    );
    scrollToIndex(targetIndex, loopItems.length - 1, behavior);
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Typography
        component="p"
        textAlign="center"
        fontSize="16px"
        fontWeight={700}
        mb={1}
      >
        {label}
      </Typography>
      <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            position: "absolute",
            top: `calc(50% - ${ITEM_HEIGHT / 2}px)`,
            left: 6,
            right: 6,
            height: `${ITEM_HEIGHT}px`,
            borderRadius: "12px",
            bgcolor: "#520A76",
            boxShadow: "0px 8px 20px rgba(82, 10, 118, 0.18)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.4) 18%, rgba(255,255,255,0) 32%, rgba(255,255,255,0) 68%, rgba(255,255,255,0.4) 82%, rgba(255,255,255,0.92) 100%)",
            pointerEvents: "none",
            zIndex: 2,
          }}
        />
        <Box
          ref={containerRef}
          onScroll={handleScroll}
          sx={{
            position: "relative",
            zIndex: 3,
            height: "100%",
            overflowY: "auto",
            scrollSnapType: "y proximity",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            touchAction: "pan-y",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Box sx={{ height: `${spacerHeight}px`, flexShrink: 0 }} />
          {loopItems.map((item, loopIndex) => {
            const normalizedItemIndex = normalizeIndex(loopIndex, items.length);
            const isSelected = item === value;
            return (
              <Box
                key={`${label}-${loopIndex}-${item}`}
                onClick={() => {
                  onChange(item);
                  scrollToLoopedIndex(normalizedItemIndex);
                }}
                sx={{
                  height: `${ITEM_HEIGHT}px`,
                  scrollSnapAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 4,
                  cursor: "pointer",
                  userSelect: "none",
                  fontSize: isSelected ? "28px" : "24px",
                  fontWeight: isSelected ? 700 : 500,
                  transition:
                    "font-size 140ms ease, opacity 140ms ease, transform 140ms ease",
                  opacity: isSelected ? 1 : 0.58,
                  transform: "translateZ(0)",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    position: "relative",
                    zIndex: 5,
                    color: isSelected ? "#FFFFFF" : "#5B6473",
                    WebkitTextFillColor: isSelected ? "#FFFFFF" : "#5B6473",
                    textShadow: isSelected
                      ? "0 0 0 rgba(255,255,255,0.01)"
                      : "none",
                    transform: "translateZ(0)",
                  }}
                >
                  {item}
                </Box>
              </Box>
            );
          })}
          <Box sx={{ height: `${spacerHeight}px`, flexShrink: 0 }} />
        </Box>
      </Box>
    </Box>
  );
}

interface TimeScrollWheelProps {
  initialHour: number;
  initialMinute: number;
  initialAmpm?: Ampm;
  is24h?: boolean;
  onSave: (hour: number, minute: number, ampm: Ampm) => void;
  saveLabel?: string;
}

function TimeScrollWheel({
  initialHour,
  initialMinute,
  initialAmpm = "AM",
  is24h = true,
  onSave,
  saveLabel,
}: TimeScrollWheelProps) {
  const { t } = useTranslation();

  const normalizedSelection = useMemo(() => {
    const safeHour = Math.max(0, Math.min(23, Number.isNaN(initialHour) ? 0 : initialHour));
    const safeMinute = Math.max(0, Math.min(55, Number.isNaN(initialMinute) ? 0 : initialMinute));

    if (is24h) {
      return {
        hourValue: pad(safeHour),
        minuteValue: pad(normalizeMinuteStep(safeMinute)),
        ampmValue: (safeHour >= 12 ? "PM" : "AM") as Ampm,
      };
    }

    const { hour12, ampm } = to12Hour(safeHour);

    return {
      hourValue: pad(hour12),
      minuteValue: pad(normalizeMinuteStep(safeMinute)),
      ampmValue: (initialAmpm || ampm) as Ampm,
    };
  }, [initialAmpm, initialHour, initialMinute, is24h]);

  const [draftHour, setDraftHour] = useState(normalizedSelection.hourValue);
  const [draftMinute, setDraftMinute] = useState(normalizedSelection.minuteValue);
  const [draftAmpm, setDraftAmpm] = useState<Ampm>(normalizedSelection.ampmValue);

  useEffect(() => {
    setDraftHour(normalizedSelection.hourValue);
    setDraftMinute(normalizedSelection.minuteValue);
    setDraftAmpm(normalizedSelection.ampmValue);
  }, [normalizedSelection]);

  const hourItems = useMemo(
    () =>
      Array.from({ length: is24h ? 24 : 12 }, (_, index) =>
        pad(is24h ? index : index + 1),
      ),
    [is24h],
  );

  const minuteItems = useMemo(
    () => Array.from({ length: 12 }, (_, index) => pad(index * 5)),
    [],
  );

  const handleSave = () => {
    const minute = parseInt(draftMinute, 10) || 0;

    if (is24h) {
      onSave(parseInt(draftHour, 10) || 0, minute, draftAmpm);
      return;
    }

    const hour12 = parseInt(draftHour, 10) || 12;
    const normalizedHour = hour12 % 12;
    const hour24 = draftAmpm === "PM" ? normalizedHour + 12 : normalizedHour;

    onSave(hour24, minute, draftAmpm);
  };

  const resetKey = `${is24h}-${normalizedSelection.hourValue}-${normalizedSelection.minuteValue}-${normalizedSelection.ampmValue}`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <Box sx={{ display: "flex", flex: 1, minHeight: 0, gap: 1.5 }}>
        <WheelColumn
          label={t("hours")}
          items={hourItems}
          value={draftHour}
          onChange={setDraftHour}
          initialIndex={hourItems.indexOf(normalizedSelection.hourValue)}
          resetKey={`${resetKey}-hour`}
        />
        <WheelColumn
          label={t("min")}
          items={minuteItems}
          value={draftMinute}
          onChange={setDraftMinute}
          initialIndex={minuteItems.indexOf(normalizedSelection.minuteValue)}
          resetKey={`${resetKey}-minute`}
        />
        {!is24h && (
          <WheelColumn
            label={"AM/PM"}
            items={["AM", "PM"]}
            value={draftAmpm}
            onChange={(value) => setDraftAmpm(value as Ampm)}
            initialIndex={["AM", "PM"].indexOf(normalizedSelection.ampmValue)}
            resetKey={`${resetKey}-ampm`}
          />
        )}
      </Box>
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          flexShrink: 0,
          pt: 2,
          pb: 1,
          bgcolor: "#fff",
          zIndex: 4,
        }}
      >
        <Button
          fullWidth
          variant="contained"
          onClick={handleSave}
          sx={{
            backgroundColor: "#520A76",
            color: "#fff",
            borderRadius: "14px",
            height: 52,
            fontSize: 16,
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { backgroundColor: "#6A1B9A" },
          }}
        >
          {saveLabel || t("save") || "Save"}
        </Button>
      </Box>
    </Box>
  );
}

export default TimeScrollWheel;
